import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Database, GitBranch, LoaderCircle, Plus } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useAuth } from '@/_shared/hooks/useAuth';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';

import { RepoCombobox } from '../components/RepoCombobox';
import { type MappingEntry, SourceCard } from '../components/SourceCard';
import { parseRepoKey } from '../lib/sources';

export function SettingsSources(): React.ReactElement {
  usePageTitle('Sources — Settings');
  const queryClient = useQueryClient();
  const { loading: authLoading } = useAuth();
  const { isAdmin, wp } = useWorkspace();
  const orpc = useOrpc();
  const [searchParams, setSearchParams] = useSearchParams();

  const sourcesQueryKey = () => orpc.settings.sources.list.queryOptions({ input: {} }).queryKey;

  // Show toast feedback from GitHub App installation redirect
  useEffect(() => {
    const error = searchParams.get('github_error');
    const success = searchParams.get('github_success');
    if (error === 'registration_failed') {
      toast.error('Failed to register GitHub App installation. Please try again.');
    } else if (error === 'missing_installation') {
      toast.error('Missing installation ID from GitHub redirect. Please try installing again.');
    } else if (error === 'not_authenticated') {
      toast.error('Your session expired. Please log in and try installing again.');
    } else if (success === 'true') {
      toast.success('GitHub App installed successfully!');
    }
    if (error || success) {
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Connect form state
  const [showConnectForm, setShowConnectForm] = useState(false);
  const [formSourceId, setFormSourceId] = useState('');
  const [formRepoKey, setFormRepoKey] = useState('');
  const [formSubfolder, setFormSubfolder] = useState('/');

  const { data, isLoading } = useQuery(orpc.settings.sources.list.queryOptions({ input: {} }));

  const sources = data?.sources ?? [];
  const githubRepos = data?.repos ?? [];
  const gitlabConnections = data?.gitlabConnections ?? [];

  // Merge GitLab projects into the repos list for the RepoCombobox
  const gitlabRepos: {
    installationId: number;
    fullName: string;
    provider: 'gitlab';
    projectId: number;
    connectionId: string;
  }[] = (data?.gitlabProjects ?? []).map((p) => ({
    installationId: 0,
    fullName: p.fullPath,
    provider: 'gitlab' as const,
    projectId: p.projectId,
    connectionId: p.connectionId,
  }));
  const repos = [...githubRepos, ...gitlabRepos];

  // Build lookup: sourceId → MappingEntry[] (1:many)
  const mappingsBySource = useMemo(() => {
    const map: Record<string, MappingEntry[]> = {};
    for (const m of data?.mappings ?? []) {
      const entry: MappingEntry = {
        installationId: m.installationId,
        repoFullName: m.repoFullName,
        subfolder: 'subfolder' in m && typeof m.subfolder === 'string' ? m.subfolder : '/',
        provider: 'provider' in m && (m.provider === 'github' || m.provider === 'gitlab') ? m.provider : 'github',
        connectionId: 'connectionId' in m && typeof m.connectionId === 'string' ? m.connectionId : undefined,
      };
      const arr = (map[m.sourceId] ??= []);
      arr.push(entry);
    }
    return map;
  }, [data?.mappings]);

  const connectedSources = sources.filter((s) => (mappingsBySource[s.id]?.length ?? 0) > 0);
  const unconnectedSources = sources.filter((s) => !mappingsBySource[s.id]?.length);

  // ─── Mutations ───

  const deleteMutation = useMutation(
    orpc.settings.sources.delete.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: sourcesQueryKey() });
        toast.success('Source deleted');
      },
      onError: () => {
        toast.error('Failed to delete source');
      },
    }),
  );

  const addMappingMutation = useMutation(
    orpc.settings.sources.addMapping.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: sourcesQueryKey() });
        toast.success('Repository linked');
      },
      onError: () => {
        toast.error('Failed to link repository');
      },
    }),
  );

  const removeMappingMutation = useMutation(
    orpc.settings.sources.removeMapping.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: sourcesQueryKey() });
        toast.success('Repository unlinked');
      },
      onError: () => {
        toast.error('Failed to unlink repository');
      },
    }),
  );

  const togglesMutation = useMutation(
    orpc.settings.sources.updateToggles.mutationOptions({
      onMutate: async (variables) => {
        await queryClient.cancelQueries({ queryKey: sourcesQueryKey() });
        const previous = queryClient.getQueryData(sourcesQueryKey());

        // Optimistic update: mutate the cached sources list
        queryClient.setQueryData(sourcesQueryKey(), (old: typeof data) => {
          if (!old) return old;
          return {
            ...old,
            sources: old.sources.map((s) =>
              s.id === variables.sourceId
                ? { ...s, docsEnabled: variables.docsEnabled, prReviewEnabled: variables.prReviewEnabled }
                : s,
            ),
          };
        });

        return { previous };
      },
      onError: (_err, _variables, context) => {
        // Rollback on error
        if (context?.previous) {
          queryClient.setQueryData(sourcesQueryKey(), context.previous);
        }
        toast.error('Failed to update toggles');
      },
      onSettled: () => {
        void queryClient.invalidateQueries({ queryKey: sourcesQueryKey() });
      },
    }),
  );

  const isSaving = addMappingMutation.isPending || removeMappingMutation.isPending;

  // ─── Handlers ───

  const handleConnect = useCallback(() => {
    if (!formSourceId || !formRepoKey) return;
    const parsed = parseRepoKey(formRepoKey);
    addMappingMutation.mutate(
      {
        sourceId: formSourceId,
        installationId: parsed.installationId,
        repoFullName: parsed.repoFullName,
        subfolder: formSubfolder || '/',
        ...(parsed.provider === 'gitlab' && {
          provider: 'gitlab' as const,
          projectId: parsed.projectId,
          connectionId: parsed.connectionId,
          baseUrl: (data?.gitlabProjects ?? []).find(
            (p) => p.connectionId === parsed.connectionId && p.projectId === parsed.projectId,
          )?.baseUrl,
        }),
      },
      {
        onSuccess: () => {
          setShowConnectForm(false);
          setFormSourceId('');
          setFormRepoKey('');
          setFormSubfolder('/');
        },
      },
    );
  }, [formSourceId, formRepoKey, formSubfolder, addMappingMutation, data?.gitlabProjects]);

  const handleAddRepo = useCallback(
    (sourceId: string, repoKey: string, subfolder: string, onSuccess: () => void) => {
      const parsed = parseRepoKey(repoKey);
      addMappingMutation.mutate(
        {
          sourceId,
          installationId: parsed.installationId,
          repoFullName: parsed.repoFullName,
          subfolder,
          ...(parsed.provider === 'gitlab' && {
            provider: 'gitlab' as const,
            projectId: parsed.projectId,
            connectionId: parsed.connectionId,
            baseUrl: (data?.gitlabProjects ?? []).find(
              (p) => p.connectionId === parsed.connectionId && p.projectId === parsed.projectId,
            )?.baseUrl,
          }),
        },
        { onSuccess },
      );
    },
    [addMappingMutation, data?.gitlabProjects],
  );

  const handleRemoveRepo = useCallback(
    (sourceId: string, repoFullName: string, provider: 'github' | 'gitlab') => {
      removeMappingMutation.mutate({ sourceId, repoFullName, provider });
    },
    [removeMappingMutation],
  );

  const handleToggle = useCallback(
    (sourceId: string, docsEnabled: boolean, prReviewEnabled: boolean) => {
      togglesMutation.mutate({ sourceId, docsEnabled, prReviewEnabled });
    },
    [togglesMutation],
  );

  const cancelConnect = useCallback(() => {
    setShowConnectForm(false);
    setFormSourceId('');
    setFormRepoKey('');
    setFormSubfolder('/');
  }, []);

  if (isLoading || authLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <LoaderCircle size={24} className="animate-spin" />
      </div>
    );
  }

  if (sources.length === 0) {
    return (
      <div className="rounded border border-amber-200/50 bg-amber-50 p-6 text-center">
        <p className="mb-2 text-sm text-amber-700">
          No RudderStack sources found. Configure your RudderStack credentials first, then sources will sync
          automatically.
        </p>
        <Link to={wp('/settings')} className="text-sm text-brand hover:underline">
          Go to General Settings
        </Link>
      </div>
    );
  }

  const hasRepos = repos.length > 0;

  return (
    <>
      {!isAdmin && (
        <div className="mb-4 rounded border border-amber-200/50 bg-amber-50 px-4 py-3 text-sm text-amber-700">
          Only workspace admins can modify settings.
        </div>
      )}

      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-sm font-bold tracking-wider text-muted-foreground uppercase">Sources</h2>
        <div className="flex items-center gap-2">
          {isAdmin && hasRepos && unconnectedSources.length > 0 && !showConnectForm && (
            <button
              onClick={() => setShowConnectForm(true)}
              className="inline-flex items-center gap-2 rounded border border-brand bg-brand/10 px-3 py-1.5 text-xs text-brand transition-colors hover:bg-brand/20"
            >
              <Plus size={12} />
              Connect Source ({unconnectedSources.length})
            </button>
          )}
        </div>
      </div>

      {/* SCM status banner — shown when no repos are available from any provider */}
      {!hasRepos && (
        <div className="mb-6 rounded border border-border bg-card p-6 text-center">
          <Database size={28} className="mx-auto mb-3 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No repositories available yet.</p>
          <p className="mt-2 text-sm text-muted-foreground">
            {gitlabConnections.length === 0 ? 'Connect GitHub or GitLab first on' : 'Configure an SCM provider on'} the{' '}
            <Link to={wp('/settings/scm')} className="text-brand hover:underline">
              Source Control
            </Link>{' '}
            page, then come back here to link repositories to sources.
          </p>
        </div>
      )}

      {/* Connect new source form */}
      {showConnectForm && (
        <section className="mb-4 rounded border border-brand/30 bg-card">
          <div className="flex items-center gap-2 border-b border-border px-4 py-3">
            <Plus size={14} className="text-brand" />
            <span className="text-sm font-medium text-foreground">Connect a Source</span>
          </div>
          <div className="space-y-3 px-4 pt-3 pb-4">
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">RudderStack Source</label>
              <select
                value={formSourceId}
                onChange={(e) => setFormSourceId(e.target.value)}
                className="w-full rounded border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-brand"
              >
                <option value="">Select a source...</option>
                {unconnectedSources.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            {formSourceId && (
              <>
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">Repository</label>
                  <RepoCombobox repos={repos} value={formRepoKey} onChange={setFormRepoKey} />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">
                    Subfolder <span className="text-muted-foreground/40">(for monorepos, default: /)</span>
                  </label>
                  <input
                    type="text"
                    value={formSubfolder}
                    onChange={(e) => setFormSubfolder(e.target.value)}
                    placeholder="/"
                    className="w-full rounded border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-brand"
                  />
                </div>
              </>
            )}
            <div className="flex items-center gap-2">
              {formSourceId && formRepoKey && (
                <button
                  type="button"
                  onClick={handleConnect}
                  disabled={!isAdmin || isSaving}
                  className="inline-flex items-center gap-2 rounded border border-brand bg-brand/10 px-3 py-1.5 text-xs text-brand transition-colors hover:bg-brand/20 disabled:opacity-30"
                >
                  {isSaving && <LoaderCircle size={12} className="animate-spin" />}
                  Connect
                </button>
              )}
              <button
                type="button"
                onClick={cancelConnect}
                className="inline-flex items-center gap-2 rounded border border-border bg-background px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-brand hover:text-brand"
              >
                Cancel
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Connected sources list */}
      {connectedSources.length === 0 && !showConnectForm ? (
        <section className="rounded border border-border bg-card p-8 text-center">
          <GitBranch size={32} className="mx-auto mb-3 text-muted-foreground/40" />
          <p className="mb-1 text-sm text-muted-foreground">No sources connected</p>
          <p className="text-xs text-muted-foreground/60">
            Connect a RudderStack source to a GitHub repository so the agent can read your tracking code.
          </p>
          {isAdmin && hasRepos && unconnectedSources.length > 0 && (
            <button
              onClick={() => setShowConnectForm(true)}
              className="mt-4 inline-flex items-center gap-2 rounded border border-brand bg-brand/10 px-4 py-2 text-sm text-brand transition-colors hover:bg-brand/20"
            >
              <Plus size={14} />
              Connect Source ({unconnectedSources.length})
            </button>
          )}
        </section>
      ) : (
        <div className="space-y-2">
          {connectedSources.map((source) => (
            <SourceCard
              key={source.id}
              source={{
                id: source.id,
                name: source.name,
                docsEnabled: source.docsEnabled ?? true,
                prReviewEnabled: source.prReviewEnabled ?? true,
              }}
              mappings={mappingsBySource[source.id] ?? []}
              repos={repos}
              isAdmin={isAdmin}
              onAddRepo={handleAddRepo}
              onRemoveRepo={handleRemoveRepo}
              onDelete={(id) => deleteMutation.mutate({ sourceId: id })}
              onToggle={handleToggle}
              saving={isSaving}
            />
          ))}
        </div>
      )}
    </>
  );
}
